import React from "react";
import { Box, Flex, rem, Skeleton, Text, useComputedColorScheme, useProps } from "@mantine/core";
import test_entry_config from "../config/test_entry_config.json";
import { getTestAxisDegrees } from "../util/TestLabelsUtil";
import VfaMapSvg, { VfaMapCellContent } from "./VfaMapSvg";
import { buildPatternGeometry, getBlindSpotDegrees } from "./vfaMapGeometry";

export const AXIS_LABEL_WIDTH = rem(32);

/**
 * Shared hatch-tile set used both for the gray scale field's own rendering
 * (grayScaleFieldImage.ts) and for the "total/pattern deviation" probability
 * tile maps, which reuse the same tiles rather than the GPA-specific set.
 */
export const NEW_DB_TILE_BASE_PATH = "img/new_db_images/{img}.png";

/**
 * A location the headset could not measure arrives as -1 and is reported as a
 * zero threshold rather than as a gap in the field.
 */
const UNMEASURED_THRESHOLD = "-1";

interface ThresholdMapProps {
  thresholds: Array<string | number>;
  testType: string;
  eye: string;
  content?: VfaMapCellContent;
  probabilityTileBasePath?: string;
  checkEmptyCells?: boolean;
  showAngleOnAxis?: boolean;
  fovealThreshold?: number | null;
  py?: any;
  bsAngles?: Record<string, unknown>;
}

export default function ThresholdMap(componentpProps: ThresholdMapProps) {
  const props = useProps("ThresholdMap", {}, componentpProps);
  const colorScheme = useComputedColorScheme("light");
  const content: VfaMapCellContent = props.content ?? "value";
  const isProbabilityTileMap = content === "probabilityTile";

  const patternConfig = props.testType
    ? test_entry_config.thresholdMap[props.testType]?.[props.eye]
    : undefined;

  const geometry = React.useMemo(
    () => (patternConfig ? buildPatternGeometry(patternConfig) : null),
    [patternConfig],
  );

  const values = React.useMemo(
    () =>
      (props.thresholds ?? []).map((value) =>
        String(value) === UNMEASURED_THRESHOLD ? "0" : value,
      ),
    [props.thresholds],
  );

  const ready = geometry != null && values.length > 0;

  const crosshairColor = isProbabilityTileMap ? "black" : "var(--threshold-border, black)";
  const invertForDarkScheme = isProbabilityTileMap && colorScheme === "dark";
  const blindSpot =
    props.eye && props.bsAngles ? getBlindSpotDegrees(props.bsAngles, props.eye) : null;

  return (
    <Skeleton visible={!ready} height={"100%"} mih={rem(200)}>
      <Flex {...props} py={props.py} align="center">
        {props.showAngleOnAxis && (
          <Flex align={"center"} justify={"center"} w={AXIS_LABEL_WIDTH} style={{ flexShrink: 0 }}>
            <Text>{getTestAxisDegrees(props.testType, props.eye === "leftEye")}</Text>
          </Flex>
        )}

        {ready && (
          <Box flex={1} miw={0} style={invertForDarkScheme ? { filter: "invert(1)" } : undefined}>
            <VfaMapSvg
              geometry={geometry}
              values={values}
              content={content}
              probabilityTileBasePath={props.probabilityTileBasePath}
              blankValueIndexes={props.checkEmptyCells ? patternConfig.emptyCellsIndex : undefined}
              crosshairColor={crosshairColor}
              fovealThreshold={props.fovealThreshold}
              blindSpot={blindSpot}
            />
          </Box>
        )}

        {props.showAngleOnAxis && (
          <Flex align={"center"} justify={"center"} w={AXIS_LABEL_WIDTH} style={{ flexShrink: 0 }}>
            <Text>{getTestAxisDegrees(props.testType, props.eye === "rightEye")}</Text>
          </Flex>
        )}
      </Flex>
    </Skeleton>
  );
}
